import { FC, useEffect, useRef, useState } from "react";
import { Modal } from "@mui/material";
import { ChevronDown, Check, DollarSign, Percent, Trash2, X } from "lucide-react";
import usePricing from "../../../hooks/usePricing";
import { useAppDispatch, useAppSelector } from "../../../hooks";
import { DiscountSliceAction } from "../../../redux/slice/discountSlice";
import { DiscountMode, IDiscountResponse } from "../../../typings";
import ManagerCodeModal from "../../common/manager-code-modal/ManagerCodeModal";

export interface IDiscountModalProps {
  open: boolean;
  onClose: () => void;
}

const NONE_DISCOUNT_ID = "none";
const ALTERATION_SERVICE_ID = -100000;

type AmountType = "percentage" | "fixed";
type Coverage = "whole" | "bill" | "custom";

const amountTypes = [
  { value: "percentage", label: "Percentage" },
  { value: "fixed", label: "Fixed" },
] as const;

const coverages = [
  { value: "whole", label: "Whole billing session" },
  { value: "bill", label: "This bill only" },
  { value: "custom", label: "Custom" },
] as const;

// Up to two decimal places, no sign - the field only ever holds a positive amount.
const AMOUNT_PATTERN = /^\d*\.?\d{0,2}$/;

function RadioRow({
  label,
  checked,
  onChange,
  disabled,
}: {
  label: string;
  checked: boolean;
  onChange: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onChange}
      disabled={disabled}
      className="flex items-center gap-[0.5rem] text-left text-[0.9375rem] font-medium text-[#515151] disabled:opacity-50"
    >
      <span className="flex h-[1.5rem] w-[1.5rem] items-center justify-center rounded-full border-[0.1875rem] border-[#515151] transition-all">
        {checked ? <span className="h-[0.75rem] w-[0.75rem] rounded-full bg-[#515151]" /> : null}
      </span>
      <span>{label}</span>
    </button>
  );
}

function FieldLabel({ children }: { children: React.ReactNode }) {
  return (
    <label className="text-[1.0625rem] font-semibold text-[#515151]">
      {children}
    </label>
  );
}

function SelectField<T extends string>({
  value,
  onChange,
  options,
  disabled,
  testName,
}: {
  value: T;
  onChange: (value: T) => void;
  options: readonly { value: T; label: string }[];
  disabled?: boolean;
  testName?: string;
}) {
  return (
    <div className="relative">
      <select
        value={value}
        onChange={(e) => onChange(e.target.value as T)}
        disabled={disabled}
        test-name={testName}
        className="h-[3rem] w-full appearance-none rounded-[0.75rem] border border-transparent bg-[#F4F4F4] px-[1rem] pr-[2.75rem] text-[0.9375rem] font-medium text-[#515151] outline-none transition focus:border-brand-secondary disabled:opacity-50"
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      <ChevronDown
        className="pointer-events-none absolute right-[0.75rem] top-1/2 -translate-y-1/2 text-[#515151]"
        size={18}
      />
    </div>
  );
}

const DiscountModal: FC<IDiscountModalProps> = ({ open, onClose }) => {
  const dispatch = useAppDispatch();
  const { categoryList } = usePricing();
  const {
    getDiscountByStoreResponse,
    selectedDiscount,
    eligibleDiscounts,
    customDiscount: appliedCustomDiscount,
    discountLocked,
    lockedDiscount,
    lockedReason,
  } = useAppSelector((state) => state.discount);
  const { cartMode, billById, selectedSession } = useAppSelector((state) => state.cart);
  const { user } = useAppSelector((state) => state.auth);

  const serviceOptions = [
    ...(categoryList?.body.filter((tab) => tab.showInMenu) || []),
    { id: ALTERATION_SERVICE_ID, categoryName: "Alteration" },
  ];

  const [discountType, setDiscountType] = useState<"defined" | "custom">("defined");
  const [isAccessModalOpen, setIsAccessModalOpen] = useState(false);
  const [definedDiscount, setDefinedDiscount] = useState<string | undefined>();
  const [amountType, setAmountType] = useState<AmountType>("percentage");
  const [coverage, setCoverage] = useState<Coverage>("whole");
  const [amount, setAmount] = useState<string>("");
  const [customServiceIds, setCustomServiceIds] = useState<number[]>([]);
  // The action waiting on manager approval; run once, then discarded so the
  // next change has to be approved again.
  const pendingApprovalAction = useRef<(() => void) | null>(null);
  const [approvalTask, setApprovalTask] = useState("apply a custom discount");

  const isDisabled = cartMode === "pickup";

  // Service types present in the bill being edited (the selected session),
  // plus Alteration when any of its items carries alteration lines - the
  // discount is calculated per line, so these decide what gets reduced.
  const billItems = selectedSession?.cartItems ?? [];
  const billServiceIds = Array.from(
    new Set([
      ...billItems
        .map((item) => item.serviceType?.id)
        .filter((id): id is number => !!id),
      ...(billItems.some((item) => item.alterationItems.length > 0)
        ? [ALTERATION_SERVICE_ID]
        : []),
    ])
  );

  const coverageServiceIds = (): number[] => {
    if (coverage === "whole") {
      return [...(categoryList?.body.map((cat) => cat.id) || []), ALTERATION_SERVICE_ID];
    }
    if (coverage === "bill") return billServiceIds;
    return customServiceIds;
  };

  const lockedServiceLabel = () => {
    if (coverage === "whole") return "All";
    if (billServiceIds.length === 0) return "No items in this bill yet";
    return billServiceIds
      .map((id) => serviceOptions.find((s) => s.id === id)?.categoryName ?? `#${id}`)
      .join(", ");
  };

  // Load whatever is currently applied each time the modal opens, so the
  // form always reflects the order rather than the last edit.
  useEffect(() => {
    if (!open) return;
    if (appliedCustomDiscount) {
      setDiscountType("custom");
      setAmountType(
        appliedCustomDiscount.discountMode === DiscountMode.FIXED ? "fixed" : "percentage"
      );
      setAmount(String(appliedCustomDiscount.discountAmount));
      setCoverage(appliedCustomDiscount.coverage ?? "whole");
      setCustomServiceIds(appliedCustomDiscount.serviceIds);
      setDefinedDiscount(NONE_DISCOUNT_ID);
      return;
    }
    setDiscountType("defined");
    setAmountType("percentage");
    setAmount("");
    setCoverage("whole");
    setCustomServiceIds([]);
    if (selectedDiscount && selectedDiscount !== NONE_DISCOUNT_ID) {
      setDefinedDiscount(selectedDiscount);
    } else if (eligibleDiscounts && eligibleDiscounts.id !== "custom-discount") {
      setDefinedDiscount(eligibleDiscounts.id);
    } else {
      setDefinedDiscount(NONE_DISCOUNT_ID);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleAmountTypeChange = (value: AmountType) => {
    setAmountType(value);
    setAmount("");
  };

  const handleAmountChange = (value: string) => {
    if (!AMOUNT_PATTERN.test(value)) return;
    if (amountType === "percentage" && Number(value) > 100) {
      setAmount("100");
      return;
    }
    setAmount(value);
  };

  const toggleCustomService = (id: number) => {
    setCustomServiceIds((prev) =>
      prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]
    );
  };

  const runWithApproval = (needsApproval: boolean, task: string, action: () => void) => {
    if (!needsApproval) {
      action();
      return;
    }
    pendingApprovalAction.current = action;
    setApprovalTask(task);
    setIsAccessModalOpen(true);
  };

  const applyDiscount = () => {
    if (discountType === "custom") {
      const customDiscountObj: IDiscountResponse = {
        id: "custom-discount",
        discountName: "Custom Discount",
        discountAmount: Number(amount),
        discountMode: amountType === "fixed" ? DiscountMode.FIXED : DiscountMode.PERCENTAGE,
        deleted: false,
        description: "Manually applied custom discount",
        createdTime: new Date().toISOString(),
        lastModifiedTime: new Date().toISOString(),
        merchantId: 0,
        storeId: 0,
        serviceIds: coverageServiceIds(),
        processingType: "ALL",
        minimumBillValue: 0,
        enabled: true,
        autoApply: false,
        applyToAnyUser: true,
        coverage,
        sessionId: coverage === "bill" ? selectedSession?.sessionId : undefined,
      };

      dispatch(DiscountSliceAction.setCustomDiscount(customDiscountObj));
      dispatch(DiscountSliceAction.setSelectedDiscount(null));
    } else if (!definedDiscount || definedDiscount === NONE_DISCOUNT_ID) {
      dispatch(DiscountSliceAction.removeDiscount());
    } else {
      dispatch(DiscountSliceAction.setSelectedDiscount(definedDiscount));
      dispatch(DiscountSliceAction.setCustomDiscount(null));
    }
    onClose();
  };

  // The predefined discount on the ticket now (picked, or the customer's
  // default applied by itself); "none" when there isn't one.
  const currentDefinedId =
    selectedDiscount && selectedDiscount !== NONE_DISCOUNT_ID
      ? selectedDiscount
      : eligibleDiscounts && eligibleDiscounts.id !== "custom-discount"
        ? String(eligibleDiscounts.id)
        : NONE_DISCOUNT_ID;

  // Client rule (2026-11-04): any discount picked, changed or removed by hand
  // needs a fresh manager approval. Applying the one already on the ticket
  // (e.g. the customer's approved default) doesn't.
  const onClickApply = () => {
    const changesPredefined =
      discountType === "defined" && (definedDiscount ?? NONE_DISCOUNT_ID) !== currentDefinedId;
    const needsApproval = discountType === "custom" || !!appliedCustomDiscount || changesPredefined;
    const task =
      discountType === "custom"
        ? "apply a custom discount"
        : (definedDiscount ?? NONE_DISCOUNT_ID) === NONE_DISCOUNT_ID
          ? "remove the discount"
          : "change the discount";
    runWithApproval(needsApproval, task, applyDiscount);
  };

  const hasActiveDiscount =
    !!appliedCustomDiscount ||
    !!eligibleDiscounts ||
    (!!selectedDiscount && selectedDiscount !== NONE_DISCOUNT_ID);

  const onClickRemove = () => {
    runWithApproval(hasActiveDiscount, "remove the discount", () => {
      dispatch(DiscountSliceAction.removeDiscount());
      onClose();
    });
  };

  const lockedServiceNames = (lockedDiscount?.serviceIds ?? [])
    .map((id) => serviceOptions.find((s) => s.id === id)?.categoryName)
    .filter(Boolean)
    .join(", ");

  const canApply =
    discountType === "defined" ||
    (Number(amount) > 0 && coverageServiceIds().length > 0);

  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        disableScrollLock
        disableRestoreFocus
        sx={{ zIndex: 999 }}
        slotProps={{
          backdrop: {
            sx: { backgroundColor: "rgba(0,0,0,0.5)" },
          },
        }}
      >
        <div className="flex h-full w-full items-center justify-center p-[2rem]">
          <div className="w-full max-w-[61.25rem] rounded-[1.75rem] border border-[#D9D9D9] bg-white shadow-[0_1.25rem_3.75rem_rgba(0,0,0,0.08)]">
            {/* Header */}
            <div className="flex items-center justify-between border-b border-[#EAEAEA] px-[2.5rem] py-[1.75rem]">
              <div>
                <h1 className="text-[2.625rem] font-bold leading-none tracking-[-0.02em] text-[#515151]">
                  Discounts
                </h1>
                <p className="mt-[0.5rem] text-[0.9375rem] text-[#515151]/70">
                  Apply either a predefined discount or create a custom discount for the ticket.
                </p>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="flex h-[2.75rem] w-[2.75rem] items-center justify-center rounded-full border-[0.125rem] border-[#515151]/70 text-[#515151] transition hover:bg-[#F7F7F7]"
                aria-label="Close"
              >
                <X size={22} strokeWidth={2.2} />
              </button>
            </div>

            {/* Body */}
            <div className="px-[2.5rem] py-[2rem]">
              {discountLocked ? (
                // Editing an invoice: its discount is shown, not changeable.
                <div test-name="discount-locked">
                  <p className="rounded-[0.75rem] bg-[#FFF4E0] px-[1rem] py-[0.75rem] text-[length:var(--fs-base)] font-medium text-[#9A5B00]">
                    {lockedReason ?? "This invoice has a payment, so its discount can't be changed."}
                  </p>
                  <div className="mt-[2rem] space-y-[1.5rem]">
                    <div className="grid gap-[1rem] lg:grid-cols-[11.25rem_minmax(0,1fr)] lg:items-center">
                      <FieldLabel>Discount</FieldLabel>
                      <div
                        test-name="locked-discount-name"
                        className="flex h-[3rem] items-center rounded-[0.75rem] bg-[#F4F4F4] px-[1rem] text-[0.9375rem] font-medium text-[#515151]"
                      >
                        {lockedDiscount ? lockedDiscount.discountName : "None"}
                      </div>
                    </div>
                    {lockedDiscount && (
                      <>
                        <div className="grid gap-[1rem] lg:grid-cols-[11.25rem_minmax(0,1fr)] lg:items-center">
                          <FieldLabel>Amount</FieldLabel>
                          <div
                            test-name="locked-discount-amount"
                            className="flex h-[3rem] items-center rounded-[0.75rem] bg-[#F4F4F4] px-[1rem] text-[0.9375rem] font-medium text-[#515151]"
                          >
                            {lockedDiscount.discountMode === DiscountMode.FIXED
                              ? `$${lockedDiscount.discountAmount.toFixed(2)}`
                              : `${lockedDiscount.discountAmount}%`}
                          </div>
                        </div>
                        <div className="grid gap-[1rem] lg:grid-cols-[11.25rem_minmax(0,1fr)] lg:items-center">
                          <FieldLabel>Service(s)</FieldLabel>
                          <div className="flex min-h-[3rem] items-center rounded-[0.75rem] bg-[#F4F4F4] px-[1rem] py-[0.5rem] text-[0.9375rem] font-medium text-[#515151]">
                            {lockedServiceNames || "—"}
                          </div>
                        </div>
                        {lockedDiscount.dormant && (
                          <p
                            test-name="locked-discount-dormant-note"
                            className="text-[length:var(--fs-base)] text-[#515151]/70"
                          >
                            {eligibleDiscounts
                              ? "Applies to the items it covers."
                              : "Not applied: it doesn't meet its rules for this invoice (disabled, or below its minimum bill)."}
                          </p>
                        )}
                      </>
                    )}
                  </div>
                  <div className="mt-[2.5rem] flex items-center justify-end border-t border-[#EAEAEA] pt-[2rem]">
                    <button
                      type="button"
                      onClick={onClose}
                      className="h-[3.5rem] rounded-[1rem] border border-[#D9D9D9] bg-white px-[1.75rem] text-[1.125rem] font-semibold text-[#515151] transition hover:bg-[#F8F8F8]"
                    >
                      Close
                    </button>
                  </div>
                </div>
              ) : (
              <>
              {/* Mode row */}
              <div className="grid gap-[2rem] lg:grid-cols-[11.25rem_minmax(0,1fr)]">
                <div className="pt-[0.25rem] text-[1.0625rem] font-semibold text-[#515151]">
                  Discount Type
                </div>
                <div className="flex flex-col gap-[1rem] sm:flex-row sm:items-center sm:gap-[2.5rem]">
                  <RadioRow
                    label="Predefined Discounts"
                    checked={discountType === "defined"}
                    onChange={() => setDiscountType("defined")}
                    disabled={isDisabled}
                  />
                  <RadioRow
                    label="Custom Discount"
                    checked={discountType === "custom"}
                    onChange={() => setDiscountType("custom")}
                    disabled={isDisabled}
                  />
                </div>
              </div>

              {discountType === "custom" ? (
                <div className="mt-[2rem] space-y-[1.5rem]">
                  {appliedCustomDiscount && (
                    <p
                      test-name="custom-discount-applied-note"
                      className="rounded-[0.75rem] bg-[#FFF4E0] px-[1rem] py-[0.75rem] text-[length:var(--fs-base)] font-medium text-[#9A5B00]"
                    >
                      A custom discount is applied to this order. Changing or removing it needs manager approval.
                    </p>
                  )}

                  <div className="grid gap-[1rem] lg:grid-cols-[11.25rem_minmax(0,1fr)] lg:items-center">
                    <FieldLabel>Amount Type</FieldLabel>
                    <SelectField
                      value={amountType}
                      onChange={handleAmountTypeChange}
                      options={amountTypes}
                      disabled={isDisabled}
                      testName="custom-discount-amount-type"
                    />
                  </div>

                  <div className="grid gap-[1rem] lg:grid-cols-[11.25rem_minmax(0,1fr)] lg:items-center">
                    <FieldLabel>Coverage</FieldLabel>
                    <SelectField
                      value={coverage}
                      onChange={setCoverage}
                      options={coverages}
                      disabled={isDisabled}
                      testName="custom-discount-coverage"
                    />
                  </div>

                  <div className="grid gap-[1rem] lg:grid-cols-[11.25rem_minmax(0,1fr)] lg:items-center">
                    <FieldLabel>Amount</FieldLabel>
                    <div className="flex h-[3rem] w-full overflow-hidden rounded-[0.75rem] border border-transparent bg-[#F4F4F4] transition focus-within:border-brand-secondary">
                      {amountType === "fixed" && (
                        <span className="flex w-[3.25rem] shrink-0 items-center justify-center border-r border-[#DADADA] bg-[#EAEAEA] text-[#3A3A3A]">
                          <DollarSign aria-hidden className="h-[1.8rem] w-[1.8rem]" strokeWidth={2} absoluteStrokeWidth />
                        </span>
                      )}
                      <input
                        type="text"
                        inputMode="decimal"
                        autoComplete="off"
                        value={amount}
                        onChange={(e) => handleAmountChange(e.target.value)}
                        disabled={isDisabled}
                        placeholder={amountType === "fixed" ? "0.00" : "0"}
                        test-name="custom-discount-amount"
                        className="h-full min-w-0 flex-1 bg-transparent px-[1rem] text-[0.9375rem] font-medium text-[#515151] outline-none disabled:opacity-50"
                      />
                      {amountType === "percentage" && (
                        <span className="flex w-[3.25rem] shrink-0 items-center justify-center border-l border-[#DADADA] bg-[#EAEAEA] text-[#3A3A3A]">
                          <Percent aria-hidden className="h-[1.8rem] w-[1.8rem]" strokeWidth={2} absoluteStrokeWidth />
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="grid gap-[1rem] lg:grid-cols-[11.25rem_minmax(0,1fr)] lg:items-start">
                    <div className="pt-[0.75rem]">
                      <FieldLabel>Service(s)</FieldLabel>
                    </div>
                    {coverage === "custom" ? (
                      <div test-name="custom-discount-services" className="flex flex-wrap gap-[0.5rem]">
                        {serviceOptions.map((s) => {
                          const checked = customServiceIds.includes(s.id);
                          return (
                            <button
                              key={s.id}
                              type="button"
                              onClick={() => toggleCustomService(s.id)}
                              disabled={isDisabled}
                              aria-pressed={checked}
                              className={`flex h-[2.75rem] items-center gap-[0.375rem] rounded-full border px-[1rem] text-[0.9375rem] font-medium transition disabled:opacity-50 ${
                                checked
                                  ? "border-brand bg-brand/10 text-brand"
                                  : "border-[#D9D9D9] bg-white text-[#515151] hover:bg-[#F8F8F8]"
                              }`}
                            >
                              {checked && <Check size={16} strokeWidth={2.6} />}
                              {s.categoryName}
                            </button>
                          );
                        })}
                      </div>
                    ) : (
                      <div
                        test-name="custom-discount-services-locked"
                        className="flex h-[3rem] items-center rounded-[0.75rem] bg-[#F4F4F4] px-[1rem] text-[0.9375rem] font-medium text-[#515151] opacity-60"
                      >
                        {lockedServiceLabel()}
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="mt-[2rem] space-y-[1.5rem]">
                  <div className="grid gap-[1rem] lg:grid-cols-[11.25rem_minmax(0,1fr)] lg:items-center">
                    <FieldLabel>Discount</FieldLabel>
                    <SelectField
                      value={definedDiscount ?? NONE_DISCOUNT_ID}
                      onChange={setDefinedDiscount}
                      options={[
                        { value: NONE_DISCOUNT_ID, label: "None" },
                        ...(getDiscountByStoreResponse?.body
                          .filter((d) => d.enabled)
                          .map((d) => ({ value: d.id, label: d.discountName })) ?? []),
                      ]}
                      disabled={isDisabled}
                      testName="defined-discount-select"
                    />
                  </div>

                  <div className="grid gap-[1rem] lg:grid-cols-[11.25rem_minmax(0,1fr)] lg:items-start">
                    <FieldLabel>Description</FieldLabel>
                    <div
                      test-name="defined-discount-description"
                      className="min-h-[8.375rem] rounded-[1rem] bg-[#F4F4F4] px-[1.25rem] py-[1rem] text-[0.9375rem] leading-[1.75rem] text-[#515151]/45"
                    >
                      {definedDiscount === NONE_DISCOUNT_ID
                        ? "No discount applied to this ticket."
                        : getDiscountByStoreResponse?.body.find(
                            (d) => d.id === definedDiscount
                          )?.description ?? ""}
                    </div>
                  </div>
                </div>
              )}

              {/* Footer */}
              <div className="mt-[2.5rem] flex items-center gap-[1rem] border-t border-[#EAEAEA] pt-[2rem]">
                {hasActiveDiscount && (
                  <button
                    type="button"
                    onClick={onClickRemove}
                    disabled={isDisabled}
                    test-name="remove-discount-button"
                    className="flex h-[3.5rem] items-center gap-[0.5rem] rounded-[1rem] border border-[#E5B3AE] bg-white px-[1.5rem] text-[1.125rem] font-semibold text-[#B42318] transition hover:bg-[#FDECEA] disabled:opacity-50"
                  >
                    <Trash2 size={18} strokeWidth={2.2} />
                    Remove Discount
                  </button>
                )}
                <div className="ml-auto flex items-center gap-[1rem]">
                  <button
                    type="button"
                    onClick={onClose}
                    className="h-[3.5rem] rounded-[1rem] border border-[#D9D9D9] bg-white px-[1.75rem] text-[1.125rem] font-semibold text-[#515151] transition hover:bg-[#F8F8F8]"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={onClickApply}
                    disabled={isDisabled || !canApply}
                    test-name="apply-discount-button"
                    className="flex h-[3.5rem] min-w-[13.75rem] items-center justify-center gap-[0.5rem] rounded-[1rem] bg-brand px-[2rem] text-[1.125rem] font-semibold text-white shadow-[0_0.625rem_1.5rem_rgb(var(--brand-primary-rgb)/0.22)] transition hover:brightness-95 active:scale-[0.99] disabled:opacity-50"
                  >
                    <Check size={20} strokeWidth={2.4} />
                    Apply
                  </button>
                </div>
              </div>
              </>
              )}
            </div>
          </div>
        </div>
      </Modal>

      <ManagerCodeModal
        open={isAccessModalOpen}
        onClose={() => {
          pendingApprovalAction.current = null;
          setIsAccessModalOpen(false);
        }}
        title="Manager Approval"
        task={approvalTask}
        onDone={() => {
          const action = pendingApprovalAction.current;
          pendingApprovalAction.current = null;
          setIsAccessModalOpen(false);
          action?.();
        }}
        actionType="DISCOUNT_DRAWER_ACCESS"
        requestedByUserId={user?.id}
        billId={billById?.id}
      />
    </>
  );
};

export default DiscountModal;
